// AdjustmentEditor.tsx
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useMemo, useRef, useState } from "react";
import { Adjustment, Dimension, RelationTarget } from "./AppTypes";
import { notifyProjectActivity } from "./api/pingClient";
import { getBaseColor, Mode } from "./colorUtils";

const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (v: string) =>
  SPECIAL_CHARACTERS.reduce((s, c) => s.split(c).join(""), v);

type Props = {
  adjustments: Adjustment[];
  onChange: (adjs: Adjustment[]) => void;
  dimensions: Dimension[];
  allNames: string[];
  isActive: boolean;
  toggle: () => void;
  userHue: number;
  mode: Mode;
  projectId: number;
  syntaxData?: any[] | null;
};

/* ---------- flat item helpers (gleich wie RelationEditor) ---------- */
type FlatItem = {
  type: "tag" | "point" | "dimension";
  id: string;
  name: string;
  dimId: string;
};

function buildFlatItems(targets: RelationTarget[], dimensions: Dimension[]): FlatItem[] {
  const items: FlatItem[] = [];
  targets.forEach((target) => {
    const dim = dimensions.find((d) => d.id === target.dimensionId);
    if (!dim) return;
    if (target.mode === "all") {
      if (!items.some((x) => x.type === "dimension" && x.dimId === dim.id))
        items.push({ type: "dimension", id: dim.id, name: dim.name, dimId: dim.id });
    } else {
      target.tagIds?.forEach((tagId) => {
        const tag = dim.tags.find((t) => t.id === tagId);
        if (tag && !items.some((x) => x.type === "tag" && x.id === tag.id))
          items.push({ type: "tag", id: tag.id, name: tag.name, dimId: dim.id });
      });
      target.pointIds?.forEach((pointId) => {
        const point = dim.points.find((p) => p.id === pointId);
        if (point && !items.some((x) => x.type === "point" && x.id === point.id))
          items.push({ type: "point", id: point.id, name: point.name, dimId: dim.id });
      });
    }
  });
  return items;
}

function rebuildTargets(items: FlatItem[]): RelationTarget[] {
  const targets: RelationTarget[] = [];
  items.forEach((item) => {
    if (item.type === "dimension") {
      targets.push({ dimensionId: item.dimId, mode: "all", tagIds: [], pointIds: [] });
      return;
    }
    let existing = targets.find((t) => t.dimensionId === item.dimId && t.mode !== "all");
    if (!existing) {
      existing = { dimensionId: item.dimId, mode: "points", tagIds: [], pointIds: [] };
      targets.push(existing);
    }
    if (item.type === "tag" && !existing.tagIds.includes(item.id)) {
      existing.tagIds.push(item.id);
      if (existing.pointIds.length === 0) existing.mode = "tags";
      else existing.mode = "points";
    } else if (item.type === "point" && !existing.pointIds.includes(item.id)) {
      existing.pointIds.push(item.id);
      existing.mode = "points";
    }
  });
  return targets;
}

function pickDropEffect(effectAllowed: string | undefined): "copy" | "move" {
  if (!effectAllowed) return "copy";
  const allowed = effectAllowed.toLowerCase();
  if (allowed === "copy") return "copy";
  if (allowed.includes("copy")) return "copy";
  return "move";
}

/* ---------- Sortable Adjustment Card ---------- */
function SortableAdjustmentCard({
  adjustment,
  expandedId,
  setExpandedId,
  onUpdate,
  onDelete,
  dimensions,
  userHue,
  mode,
  allNames,
  syntaxStat,
}: {
  adjustment: Adjustment;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<Adjustment>) => void;
  onDelete: (id: string) => void;
  dimensions: Dimension[];
  userHue: number;
  mode: Mode;
  allNames: string[];
  syntaxStat?: { syntax: string; SyntaxMessages: string[] } | null;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: adjustment.id });

  const cardStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    // off = warmes Rot-Orange, pin = Blau
    background: getBaseColor(4, adjustment.type === "off" ? "disconnection" : "connection", userHue, mode),
  };

  const expanded = expandedId === adjustment.id;
  const isDuplicate = allNames.filter((n) => n === adjustment.name).length > 1;
  const [isEditingName, setIsEditingName] = useState(false);
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(null);
  const lastDropRef = useRef(0);

  const allItems = buildFlatItems(adjustment.targets, dimensions);

  /* ---- remove helpers ---- */
  const removeItem = (item: FlatItem) => {
    if (item.type === "dimension") {
      onUpdate(adjustment.id, { targets: adjustment.targets.filter((t) => t.dimensionId !== item.dimId) });
    } else if (item.type === "tag") {
      onUpdate(adjustment.id, {
        targets: adjustment.targets
          .map((t) =>
            t.tagIds?.includes(item.id)
              ? { ...t, tagIds: t.tagIds.filter((id) => id !== item.id) }
              : t
          )
          .filter((t) => t.mode === "all" || t.tagIds.length > 0 || t.pointIds.length > 0),
      });
    } else {
      onUpdate(adjustment.id, {
        targets: adjustment.targets
          .map((t) =>
            t.pointIds?.includes(item.id)
              ? { ...t, pointIds: t.pointIds.filter((id) => id !== item.id) }
              : t
          )
          .filter((t) => t.mode === "all" || t.tagIds.length > 0 || t.pointIds.length > 0),
      });
    }
  };

  /* ---- add helpers ---- */
  const addToTarget = (dimId: string, type: "tag" | "point", id: string) => {
    const existingIdx = adjustment.targets.findIndex((t) => t.dimensionId === dimId);
    if (existingIdx !== -1) {
      onUpdate(adjustment.id, {
        targets: adjustment.targets.map((t, i) => {
          if (i !== existingIdx) return t;
          if (type === "point") {
            const pts = t.mode === "points" ? t.pointIds : [];
            return { ...t, mode: "points" as const, pointIds: pts.includes(id) ? pts : [...pts, id], tagIds: [] };
          }
          const tags = t.mode === "tags" ? t.tagIds : [];
          return { ...t, mode: "tags" as const, tagIds: tags.includes(id) ? tags : [...tags, id], pointIds: [] };
        }),
      });
    } else {
      onUpdate(adjustment.id, {
        targets: [
          ...adjustment.targets,
          { dimensionId: dimId, mode: type === "tag" ? "tags" : "points", tagIds: type === "tag" ? [id] : [], pointIds: type === "point" ? [id] : [] },
        ],
      });
    }
  };

  const addDimension = (dimId: string) => {
    const exists = adjustment.targets.some((t) => t.dimensionId === dimId);
    if (!exists) {
      onUpdate(adjustment.id, { targets: [...adjustment.targets, { dimensionId: dimId, mode: "all", tagIds: [], pointIds: [] }] });
    } else {
      onUpdate(adjustment.id, {
        targets: adjustment.targets.map((t) =>
          t.dimensionId === dimId ? { ...t, mode: "all" as const, tagIds: [], pointIds: [] } : t
        ),
      });
    }
  };

  const reorderItem = (dragged: FlatItem, target: FlatItem) => {
    const dragIdx = allItems.findIndex((i) => i.type === dragged.type && i.id === dragged.id);
    const targetIdx = allItems.findIndex((i) => i.type === target.type && i.id === target.id);
    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) return;
    const reordered = [...allItems];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    onUpdate(adjustment.id, { targets: rebuildTargets(reordered) });
  };

  const backendErrors = syntaxStat?.SyntaxMessages;
  const hasBackendError = backendErrors && backendErrors.length > 0;
  const errorMessage = hasBackendError
    ? (Array.isArray(backendErrors) ? backendErrors.map(e => String(e)).join(", ") : String(backendErrors))
    : null;
  const isValid = !errorMessage;
  const validationIcon = !isValid && errorMessage && /warning/i.test(errorMessage) ? "❗️" : "⚠️";

  const buttonStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    background: mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
    borderRadius: 14,
    fontSize: "14px",
    fontWeight: 500,
    color: mode === "dark" ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)",
    cursor: "grab",
    userSelect: "none",
    transition: "all 0.15s ease",
  };

  const inputStyle: React.CSSProperties = {
    padding: "5px 8px",
    borderRadius: 6,
    border: mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.1)",
    background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)",
    color: "inherit",
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...cardStyle, margin: "8px 0", padding: 8, borderRadius: 6, color: "inherit" }}
      onDragOver={(e) => {
        if (!expanded) { e.stopPropagation(); return; }
        if (e.dataTransfer.types.includes("application/x-zeitgeist") || e.dataTransfer.types.includes("application/x-zeitgeist-delete")) {
          e.preventDefault();
          e.dataTransfer.dropEffect = pickDropEffect(e.dataTransfer.effectAllowed);
        }
      }}
      onDrop={(e) => {
        if (!expanded) { e.stopPropagation(); return; }
        if (e.dataTransfer.types.includes("application/x-zeitgeist-delete")) { e.preventDefault(); e.stopPropagation(); return; }
        const data = e.dataTransfer.getData("application/x-zeitgeist");
        if (!data) return;
        try {
          const payload = JSON.parse(data);
          if (payload.kind === "create-condition-from-dimension-point") { e.preventDefault(); e.stopPropagation(); addToTarget(payload.dimensionId, "point", payload.pointId); }
          else if (payload.kind === "create-condition-from-dimension-tag") { e.preventDefault(); e.stopPropagation(); addToTarget(payload.dimensionId, "tag", payload.tagId); }
          else if (payload.kind === "create-condition-from-dimension") { e.preventDefault(); e.stopPropagation(); addDimension(payload.dimensionId); }
        } catch (err) { console.error(err); }
      }}
      {...attributes}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div
          {...listeners}
          style={{ cursor: "grab" }}
          title="Drag to reorder · Drag out to remove"
        >
          ☰
        </div>
        <span style={{ cursor: "pointer", padding: "0 6px", userSelect: "none" }} onClick={() => setExpandedId(expanded ? null : adjustment.id)}>
          {expanded ? "▾" : "▸"}
        </span>

        {isEditingName ? (
          <>
            <input
              type="text"
              value={adjustment.name}
              onChange={(e) => {
                const raw = e.target.value;
                const stripped = stripSpecialChars(raw);
                if (stripped !== raw) {
                  const found = SPECIAL_CHARACTERS.filter((c) => raw.includes(c)).map((c) => (c === "\n" ? "\u21b5" : c)).join(", ");
                  setSpecialCharWarning(found);
                  setTimeout(() => setSpecialCharWarning(null), 2000);
                }
                onUpdate(adjustment.id, { name: stripped });
              }}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => { if (e.key === "Enter") setIsEditingName(false); }}
              autoFocus
              onFocus={(e) => e.target.select()}
              className={`dimension-card__input ${isDuplicate ? "dimension-card__input--duplicate" : ""}`}
              style={{ flex: 1 }}
            />
            {specialCharWarning && (
              <span style={{ color: "#e55", fontSize: "0.75em", whiteSpace: "nowrap" }}>"{specialCharWarning}" nicht erlaubt</span>
            )}
          </>
        ) : (
          <div style={{ flex: 1, userSelect: "none" }} onDoubleClick={() => setIsEditingName(true)}>
            {adjustment.name}
          </div>
        )}

        {errorMessage && (
          <span
            title={errorMessage}
            style={{ marginLeft: 4, cursor: "help", fontSize: "0.9em" }}
          >
            {validationIcon}
          </span>
        )}
      </div>

      {isDuplicate && <div style={{ color: "#d66", fontSize: "0.8em", marginTop: 4 }}>Name already in use</div>}

      {expanded && (
        <div style={{ marginTop: 8 }}>
          {errorMessage && (
            <div
              style={{
                background: "rgba(255,140,0,0.15)",
                border: "1px solid rgba(255,140,0,0.4)",
                borderRadius: 6,
                padding: "6px 10px",
                marginBottom: 8,
                fontSize: "0.85em",
                color: mode === "dark" ? "#ffb347" : "#b85c00",
              }}
            >
              {errorMessage}
            </div>
          )}
          {/* Type selector */}
          <div style={{ marginBottom: 10 }}>
            <label>
              Type:{" "}
              <select
                value={adjustment.type}
                onChange={(e) => onUpdate(adjustment.id, { type: e.target.value as "off" | "pin" })}
              >
                <option value="off">off</option>
                <option value="pin">pin</option>
              </select>
            </label>
          </div>

          {/* Datumsfelder */}
          {adjustment.type === "off" ? (
            <div className="dimension-card__section" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
              <label className="attr-label">
                <span>start_date:</span>
                <input
                  type="date"
                  value={adjustment.startDate ?? ""}
                  onChange={(e) => onUpdate(adjustment.id, { startDate: e.target.value || undefined })}
                  style={inputStyle}
                />
              </label>
              <label className="attr-label">
                <span>end_date:</span>
                <input
                  type="date"
                  value={adjustment.endDate ?? ""}
                  onChange={(e) => onUpdate(adjustment.id, { endDate: e.target.value || undefined })}
                  style={inputStyle}
                />
              </label>
            </div>
          ) : (
            <div className="dimension-card__section" style={{ marginBottom: 10 }}>
              <label className="attr-label">
                <span>date:</span>
                <input
                  type="date"
                  value={adjustment.date ?? ""}
                  onChange={(e) => onUpdate(adjustment.id, { date: e.target.value || undefined })}
                  style={inputStyle}
                />
              </label>
            </div>
          )}

          {/* Drop Zone */}
          <div
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes("application/x-zeitgeist") || e.dataTransfer.types.includes("application/x-adj-delete")) {
                e.preventDefault();
                e.currentTarget.style.borderColor = "#4ade80";
                e.currentTarget.style.background = mode === "dark" ? "rgba(74,222,128,0.12)" : "rgba(74,222,128,0.08)";
              }
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.borderColor = mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
              e.currentTarget.style.background = mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)";
            }}
            onDrop={(e) => {
              e.currentTarget.style.borderColor = mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
              e.currentTarget.style.background = mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)";
              if (e.dataTransfer.types.includes("application/x-adj-delete")) { e.preventDefault(); e.stopPropagation(); return; }
              const data = e.dataTransfer.getData("application/x-zeitgeist");
              if (!data) return;
              e.preventDefault(); e.stopPropagation();
              try {
                const payload = JSON.parse(data);
                if (payload.kind === "create-condition-from-dimension-point") addToTarget(payload.dimensionId, "point", payload.pointId);
                else if (payload.kind === "create-condition-from-dimension-tag") addToTarget(payload.dimensionId, "tag", payload.tagId);
                else if (payload.kind === "create-condition-from-dimension") addDimension(payload.dimensionId);
              } catch (err) { console.error(err); }
            }}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", minHeight: 36,
              border: mode === "dark" ? "1px dashed rgba(255,255,255,0.1)" : "1px dashed rgba(0,0,0,0.08)",
              borderRadius: 8,
              background: mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
              transition: "all 0.15s ease",
            }}
          >
            <span style={{ fontSize: "0.7em", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.65)", minWidth: 42, flexShrink: 0 }}>
              Points
            </span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
              {allItems.map((item) => (
                <div
                  key={`${item.type}-${item.id}`}
                  draggable
                  onDragStart={(e) => {
                    const payload = { type: item.type, id: item.id, dimId: item.dimId, name: item.name, kind: "adj-reorder" };
                    e.dataTransfer.setData("application/x-adj-delete", JSON.stringify(payload));
                    e.dataTransfer.setData("text/plain", JSON.stringify(payload));
                    e.dataTransfer.effectAllowed = "move";
                    (e.currentTarget as HTMLElement).style.opacity = "0.4";
                  }}
                  onDragEnd={(e) => {
                    (e.currentTarget as HTMLElement).style.opacity = "1";
                    if (e.dataTransfer.dropEffect === "none" && Date.now() - lastDropRef.current > 500) removeItem(item);
                  }}
                  onDragOver={(e) => {
                    if (e.dataTransfer.types.includes("application/x-adj-delete")) {
                      e.preventDefault(); e.stopPropagation();
                      e.dataTransfer.dropEffect = "move";
                      (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                      (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
                    }
                  }}
                  onDragLeave={(e) => { (e.currentTarget as HTMLElement).style.outline = ""; (e.currentTarget as HTMLElement).style.outlineOffset = ""; }}
                  onDrop={(e) => {
                    (e.currentTarget as HTMLElement).style.outline = "";
                    lastDropRef.current = Date.now();

                    // Externer Drop (aus Plan/Sidebar) soll von der Zone verarbeitet werden,
                    // nicht vom einzelnen Chip – sonst klappt der Dimension-Drop oft erst beim 2. Versuch.
                    const isExternalZeitgeistDrop =
                      e.dataTransfer.types.includes("application/x-zeitgeist") &&
                      !e.dataTransfer.types.includes("application/x-adj-delete");
                    if (isExternalZeitgeistDrop) {
                      return;
                    }

                    const raw = e.dataTransfer.getData("application/x-adj-delete") || e.dataTransfer.getData("text/plain");
                    if (!raw) return;
                    e.preventDefault(); e.stopPropagation();
                    try {
                      const dragged: FlatItem & { kind?: string } = JSON.parse(raw);
                      if (dragged.kind && dragged.kind !== "adj-reorder") return;
                      if (dragged.type === item.type && dragged.id === item.id) return;
                      reorderItem(dragged, item);
                    } catch (err) { console.error(err); }
                  }}
                  style={buttonStyle}
                  title="Drag to reorder · Drag out to remove"
                >
                  {item.name}
                </div>
              ))}
              {allItems.length === 0 && (
                <span style={{ color: "rgba(128,128,128,0.5)", fontStyle: "italic", fontSize: "0.9em" }}>Drag points here</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Hauptkomponente ---------- */
export default function AdjustmentEditor({
  adjustments,
  onChange,
  dimensions,
  allNames,
  isActive,
  toggle,
  userHue,
  mode,
  projectId,
  syntaxData,
}: Props) {
  const adjustmentSyntaxMap = useMemo(() => {
    const map = new Map<string, { syntax: string; SyntaxMessages: string[] }>();
    if (!syntaxData) return map;
    syntaxData.forEach((entry: any) => {
      if (entry.adjustmentName) map.set(entry.adjustmentName, { syntax: entry.syntax, SyntaxMessages: entry.SyntaxMessages ?? [] });
    });
    return map;
  }, [syntaxData]);
  const getAdjustmentSortDate = (adj: Adjustment): string => adj.startDate ?? adj.date ?? "";
  const sortAdjustmentsNewestFirst = (list: Adjustment[]): Adjustment[] =>
    [...list].sort((a, b) => {
      const aDate = getAdjustmentSortDate(a);
      const bDate = getAdjustmentSortDate(b);
      if (aDate !== bDate) return bDate.localeCompare(aDate);
      return b.id.localeCompare(a.id);
    });

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeAdjId, setActiveAdjId] = useState<string | null>(null);
  const editorFrameRef = useRef<HTMLDivElement | null>(null);
  const sortedAdjustments = sortAdjustmentsNewestFirst(adjustments);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.relatedTarget === null) document.dispatchEvent(new MouseEvent("mouseup", { bubbles: true }));
    };
    window.addEventListener("mouseleave", handleMouseLeave);
    return () => window.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  const handleAdd = () => {
    const newAdj: Adjustment = {
      id: Date.now().toString(),
      name: "Adjustment" + (adjustments.length + 1),
      type: "off",
      targets: [],
    };
    onChange(sortAdjustmentsNewestFirst([...adjustments, newAdj]));
    setExpandedId(newAdj.id);
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleUpdate = (id: string, updates: Partial<Adjustment>) => {
    onChange(sortAdjustmentsNewestFirst(adjustments.map((a) => (a.id === id ? { ...a, ...updates } : a))));
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleDelete = (id: string) => {
    onChange(adjustments.filter((a) => a.id !== id));
    if (expandedId === id) setExpandedId(null);
    notifyProjectActivity(projectId).catch(() => {});
  };

  return (
    <div
      className="relation-editor"
      ref={editorFrameRef}
      style={{ background: getBaseColor(2, "section", userHue, mode) }}
    >
      <div className="relation-editor__header" onClick={toggle}>
        <strong className="relation-editor__title">
          {isActive ? "▾" : "▸"} Adjustments
        </strong>
      </div>

      {isActive && (
        <div className="relation-editor__body">
          <button onClick={handleAdd} className="relation-editor__add" style={{ marginBottom: 10 }}>
            + Adjustment
          </button>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => { setExpandedId(null); setActiveAdjId(String(e.active.id)); }}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              setActiveAdjId(null);
              const finalRect = active.rect.current.translated ?? active.rect.current.initial;
              const editorRect = editorFrameRef.current?.getBoundingClientRect();
              if (finalRect && editorRect &&
                (finalRect.right <= editorRect.left || finalRect.left >= editorRect.right ||
                  finalRect.bottom <= editorRect.top || finalRect.top >= editorRect.bottom)) {
                handleDelete(String(active.id));
                return;
              }

              if (active.id !== over?.id) {
                const oldIndex = sortedAdjustments.findIndex((a) => a.id === active.id);
                const newIndex = sortedAdjustments.findIndex((a) => a.id === over?.id);
                if (oldIndex !== -1 && newIndex !== -1) {
                  onChange(arrayMove(sortedAdjustments, oldIndex, newIndex));
                }
              }
            }}
          >
            <SortableContext items={sortedAdjustments.map((a) => a.id)} strategy={verticalListSortingStrategy}>
              {sortedAdjustments.map((adj) => (
                <SortableAdjustmentCard
                  key={adj.id}
                  adjustment={adj}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  dimensions={dimensions}
                  userHue={userHue}
                  mode={mode}
                  allNames={allNames}
                  syntaxStat={adjustmentSyntaxMap.get(adj.name)}
                />
              ))}
            </SortableContext>

            <DragOverlay>
              {activeAdjId ? (
                <div className="drag-overlay-card" style={{ background: getBaseColor(4, sortedAdjustments.find((a) => a.id === activeAdjId)?.type === "off" ? "disconnection" : "connection", userHue, mode) }}>
                  <div className="editor-card__drag">☰</div>
                  <div style={{ flex: 1 }}>{sortedAdjustments.find((a) => a.id === activeAdjId)?.name ?? ""}</div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}
    </div>
  );
}
